import type React from 'react'
import { useEffect, useRef, useState } from 'react'

import { stopPropagation } from '@/helpers'

type Props = {
  name: string
  editing: boolean
  className: string
  inputClassName: string
  /** The name typed in: on blur or on Enter, once. */
  onRename: (name: string) => void
  /** Enter: the caller decides what the card does next. */
  onSubmit: () => void
}

export const Name: React.FC<Props> = ({
  name,
  editing,
  className,
  inputClassName,
  onRename,
  onSubmit,
}) => {
  const [value, setValue] = useState(name)
  const done = useRef(false)

  useEffect(() => {
    setValue(name)
  }, [name])

  useEffect(() => {
    if (editing) {
      done.current = false
    }
  }, [editing])

  const resize = (e: HTMLTextAreaElement) => {
    e.style.height = 'auto'
    e.style.height = `${e.scrollHeight}px`
  }

  const onInit = (e: HTMLTextAreaElement | null) => {
    if (e) {
      resize(e)

      setTimeout(() => {
        e.focus()
        e.setSelectionRange(e.value.length, e.value.length)
      }, 10)
    }
  }

  const commit = () => {
    if (done.current) {
      return
    }

    done.current = true
    onRename(value)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!e.shiftKey && e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      commit()
      onSubmit()
    }
  }

  if (!editing) {
    return <div className={className}>{name}</div>
  }

  return (
    <textarea
      rows={1}
      ref={onInit}
      placeholder="Block name"
      className={inputClassName}
      value={value}
      onClick={stopPropagation}
      onChange={(e) => {
        setValue(e.target.value)
        resize(e.currentTarget)
      }}
      onBlur={commit}
      onKeyDown={onKeyDown}
    />
  )
}
